import { FileSpreadsheet, Upload, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const HistoricalImportModal = ({
  isOpen,
  onClose,
  accounts,
  selectedAccountId,
  setSelectedAccountId,
  selectedFile,
  setSelectedFile,
  onImport,
  isImporting,
  onViewHistory,
}) => {
  const handleClose = () => {
    onClose(false);
    setSelectedFile(null);
    setSelectedAccountId("");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-charcoal border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-purple-400" />
            Import Historical Trades
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Import closed trades from an Excel file. Each sheet should be named: Symbol-Strategy-Exchange (e.g., BEL-Swing-NSE)
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label className="text-slate-300 mb-2 block">Select Account *</Label>
            <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
              <SelectTrigger 
                className="w-full bg-black/20 border-white/10 text-white" 
                data-testid="historical-account-select"
              >
                <SelectValue placeholder="Select account for import" />
              </SelectTrigger>
              <SelectContent className="bg-charcoal border-white/10">
                <SelectItem value="none" disabled>Select account</SelectItem>
                {accounts.map(account => (
                  <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="text-slate-300 mb-2 block">Excel File *</Label>
            <div className="border-2 border-dashed border-white/20 rounded-lg p-6 text-center hover:border-purple-400/50 transition-colors">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="hidden"
                id="historical-file-input"
                data-testid="historical-file-input"
              />
              <label htmlFor="historical-file-input" className="cursor-pointer">
                <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                {selectedFile ? (
                  <p className="text-purple-400 font-medium">{selectedFile.name}</p>
                ) : (
                  <p className="text-slate-400">Click to select Excel file</p>
                )}
              </label>
            </div>
          </div>
          
          <div className="bg-purple-500/10 border border-purple-500/20 rounded-md p-3 text-sm text-slate-300">
            <p className="font-medium text-purple-400 mb-1">Expected Excel Format:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>One sheet per symbol (Sheet name: Symbol-Strategy-Exchange)</li>
              <li>Columns: Entry Date, Trade Type (BUY/SELL), Entry Point, Exit Date, Exit Point, # Lots, Lot Size, P/L Points, Total Points, Comments</li>
              <li>Comments format: "Exit Comment / Entry Comment"</li>
            </ul>
          </div>
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={onViewHistory}
            className="border-white/10 text-slate-300 hover:bg-white/10 mr-auto"
            data-testid="view-import-history-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            View Import History
          </Button>
          <Button 
            variant="outline" 
            onClick={handleClose}
            className="border-white/10 text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button 
            onClick={onImport}
            disabled={isImporting || !selectedAccountId || selectedAccountId === "none" || !selectedFile}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="historical-import-btn"
          >
            {isImporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Import Trades
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HistoricalImportModal;
